import React from 'react';
import { Heart, Target, Shield, Globe, Lightbulb, Users, Award, Leaf, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutPage = ({ onBack }) => {
  const { t } = useTranslation();
  const values = [
    {
      icon: Lightbulb,
      title: t('about.innovation'),
      description: t('about.innovationDesc')
    },
    {
      icon: Users,
      title: t('about.accessibility'),
      description: t('about.accessibilityDesc')
    },
    {
      icon: Shield,
      title: t('about.trust'),
      description: t('about.trustDesc')
    },
    {
      icon: Globe,
      title: t('about.inclusivity'),
      description: t('about.inclusivityDesc')
    },
    {
      icon: Award,
      title: t('about.empowerment'),
      description: t('about.empowermentDesc')
    },
    {
      icon: Heart,
      title: t('about.compassion'),
      description: t('about.compassionDesc')
    },
    {
      icon: Target,
      title: t('about.resultsDriven'),
      description: t('about.resultsDrivenDesc')
    },
    {
      icon: Leaf,
      title: t('about.sustainability'),
      description: t('about.sustainabilityDesc')
    }
  ];

  const stats = [
    { number: "50,000+", label: t('about.activeUsers') },
    { number: "5M+", label: t('about.mealsScanned') },
    { number: "2M+", label: "Workouts" },
    { number: "500M+", label: t('wearable.calories') }
  ];

  const promises = [
    "Make tracking effortless – Snap a photo, and we handle the rest",
    "Provide honest guidance – Based on science, not fads",
    "Protect your privacy – Your data belongs to you, always",
    "Support you 24/7 – With AI coaching and responsive human support",
    "Keep improving – Your feedback shapes our future",
    "Celebrate your progress – Every step forward matters"
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="bg-black text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            {t('about.backToHome')}
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('about.title')}</h1>
          <p className="text-xl text-gray-300">
            Empowering everyone to achieve their health goals through intelligent technology
          </p>
        </div>
      </div>

      {/* Who We Are */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#FF6600]/10 rounded-full flex items-center justify-center">
              <Users className="text-[#FF6600]" size={24} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">{t('about.ourStory')}</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              {t('about.ourStoryP1')}
            </p>
            <p>
              {t('about.ourStoryP2')}
            </p>
            <p>
              {t('about.ourStoryP3')}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-xl text-center">
              <span className="text-2xl">🏥</span>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Health Enthusiasts</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-xl text-center">
              <span className="text-2xl">🤖</span>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Tech Innovators</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-xl text-center">
              <span className="text-2xl">🌍</span>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Global Community</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-xl text-center">
              <span className="text-2xl">💪</span>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Wellness Partners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-white dark:bg-gray-800/20 rounded-full flex items-center justify-center">
              <Target size={24} />
            </div>
            <h2 className="text-3xl font-bold">{t('about.ourMission')}</h2>
          </div>
          
          <p className="text-2xl md:text-3xl font-semibold mb-8 leading-relaxed">
            "To empower everyone to achieve their health goals through intelligent, effortless nutrition and fitness tracking."
          </p>

          <div className="grid md:grid-cols-2 gap-6 text-left mt-12">
            <div className="bg-white dark:bg-gray-800/10 backdrop-blur-sm p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2">🎯 Health should be simple</h3>
              <p className="text-orange-100">Not everyone has time to read nutrition labels or calculate macros. Our AI does the hard work so you can focus on living.</p>
            </div>
            <div className="bg-white dark:bg-gray-800/10 backdrop-blur-sm p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2">🤖 Technology should serve people</h3>
              <p className="text-orange-100">We harness AI not to replace human connection, but to make expert-level nutrition guidance available to all.</p>
            </div>
            <div className="bg-white dark:bg-gray-800/10 backdrop-blur-sm p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2">❤️ Everyone deserves support</h3>
              <p className="text-orange-100">Whether you're a busy parent, dedicated athlete, or just starting out, you deserve tools that adapt to YOUR life.</p>
            </div>
            <div className="bg-white dark:bg-gray-800/10 backdrop-blur-sm p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2">🌱 Small changes, big results</h3>
              <p className="text-orange-100">We don't believe in extreme diets or quick fixes. We believe in sustainable habits that transform your health over time.</p>
            </div>
          </div>

          <div className="mt-12 p-6 bg-white dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl">
            <p className="text-xl font-semibold">
              Our goal is to help <span className="text-3xl font-bold">10 million people</span> live healthier lives by 2030
            </p>
            <p className="text-orange-100 mt-2">– one meal, one workout, one healthy choice at a time.</p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('about.ourValues')}</h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 border border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-[#FF6600]/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="text-[#FF6600]" size={24} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{value.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Our Community Impact</h2>
            <p className="text-gray-600 dark:text-gray-300">Join thousands who have transformed their health with NutriFitness AI</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-sm">
                <p className="text-3xl md:text-4xl font-bold text-[#FF6600]">{stat.number}</p>
                <p className="text-gray-600 dark:text-gray-300 text-sm mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Our Promise to You</h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">When you join NutriFitness AI, you're gaining a partner in your health journey</p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-4">
              {promises.map((promise, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-[#FF6600] flex-shrink-0 mt-1" size={20} />
                  <p className="text-white">{promise}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 px-4 bg-[#FF6600]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <p className="text-2xl md:text-3xl font-semibold italic">
            "At NutriFitness AI, we don't just count calories – we make calories count."
          </p>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Get in Touch</h2>
          <div className="flex flex-col md:flex-row justify-center gap-6">
            <a 
              href="https://www.nutrifitness.us" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF6600] text-white font-semibold rounded-full hover:bg-[#E55C00] transition-colors"
            >
              <Globe size={20} />
              www.nutrifitness.us
            </a>
            <a 
              href="mailto:support@nutrifitness.us"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-colors"
            >
              <Heart size={20} />
              support@nutrifitness.us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
